import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button } from '@exam-prep/ui';
import { t } from '@exam-prep/i18n';
import { useLoad } from '../../lib/useLoad';
import { api } from '../../lib/api';
import type { Instructions as Ins, AttemptView } from '../../lib/api-types';
import { ErrorBox, Loading } from '../../components/Feedback';

export function Instructions({
  mockId,
  onStarted,
}: {
  mockId: string;
  onStarted: (attemptId: string) => void;
}) {
  const { data, error, reload } = useLoad(() => api<Ins>('GET', `/app/mocks/${mockId}`), [mockId]);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [startError, setStartError] = useState<unknown>(null);
  if (error) return <ErrorBox error={error} retry={reload} />;
  if (!data) return <Loading />;
  const timed = data.paper.units.length > 1;
  const start = async () => {
    setBusy(true);
    setStartError(null);
    try {
      const v = await api<AttemptView>('POST', `/app/mocks/${mockId}/attempts`);
      onStarted(v.attemptId);
    } catch (e) {
      setStartError(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader>
          <p className="text-xs font-semibold tracking-[0.14em] text-muted-foreground uppercase">
            {t('miniapp.ins.title')}
          </p>
          <CardTitle className="text-xl">{data.mock.title}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="inline-flex rounded-full border border-input px-3 py-1 font-display font-semibold">
            {t('miniapp.mock.meta', {
              questions: data.paper.questions,
              minutes: data.paper.durationMin,
            })}
          </p>
          <div>
            <p className="mb-2 font-display font-semibold">{t('miniapp.ins.sections')}</p>
            <ul className="space-y-2">
              {data.sections.map((s) => (
                <li key={s.key} className="rounded-xl border bg-card p-3">
                  <span className="block font-medium">
                    {s.subjectName} · {s.questions}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {s.qualifying
                      ? t('miniapp.ins.qualifying')
                      : t('miniapp.ins.marking', { marks: s.marks, negative: s.negative })}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          <p>
            {timed
              ? t('miniapp.ins.timers', {
                  parts: data.paper.units.map((u) => `${String(u.durationMin)} min`).join(', '),
                })
              : t('miniapp.ins.timer_one')}
          </p>
          {data.paper.ui.calculator ? <p>{t('miniapp.ins.calculator')}</p> : null}
          {data.mock.ranked ? <p>{t('miniapp.ins.ranked')}</p> : null}
          <p>{t('miniapp.ins.offline')}</p>
          <label className="tap-row cursor-pointer" data-selected={agree}>
            <input
              type="checkbox"
              className="size-5 shrink-0"
              checked={agree}
              onChange={(e) => setAgree(e.target.checked)}
            />
            {t('miniapp.ins.agree')}
          </label>
          {startError ? <ErrorBox error={startError} /> : null}
          <Button
            size="lg"
            className="w-full"
            disabled={!agree || busy}
            pending={busy}
            pendingOverlay={t('miniapp.loading')}
            onClick={() => void start()}
          >
            {t('miniapp.ins.begin')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
